import type { CampaignListInput, CampaignRepository, CreateCampaignInput, PendingContribution, TenantContext } from '@zetyar/data-contracts'
import type { Campaign, CampaignStatus, UUID } from '@zetyar/domain'
import type { SupabaseClient } from '@supabase/supabase-js'

export function createSupabaseCampaignRepository(client: SupabaseClient): CampaignRepository {
  return {
    async listVisible(context: TenantContext, { limit }: CampaignListInput) {
      const { data, error } = await client
        .from('campaigns')
        .select('id, organization_id, title, description, goal_amount, raised_amount, contributor_count, currency, status, visibility, start_date, end_date, created_at, updated_at')
        .eq('organization_id', context.organizationId)
        .is('deleted_at', null)
        .order('created_at', { ascending: false })
        .limit(Math.max(1, Math.min(limit, 200)))

      if (error) throw error

      return (data ?? []).map((item): Campaign => {
        const goal = Number(item.goal_amount)
        const raised = Number(item.raised_amount)
        return {
          id: item.id as UUID,
          organizationId: item.organization_id as UUID,
          title: item.title as string,
          description: item.description as string,
          imageUrl: null,
          goalAmount: goal,
          raisedAmount: raised,
          contributorCount: item.contributor_count as number,
          currency: item.currency as string,
          status: item.status as Campaign['status'],
          visibility: item.visibility as Campaign['visibility'],
          startDate: (item.start_date as string | null) ?? null,
          endDate: (item.end_date as string | null) ?? null,
          createdBy: '',
          createdAt: item.created_at as string,
          updatedAt: item.updated_at as string,
          deletedAt: null,
          progressPercentage: goal > 0 ? Math.min(100, Math.round((raised / goal) * 100)) : 0,
        }
      })
    },

    async contribute(context: TenantContext, campaignId: UUID, amount: number, isAnonymous?: boolean, message?: string, payerEmail?: string) {
      if (!context.membershipId) throw new Error('Membership is required to contribute to a campaign')

      const { data, error } = await client.functions.invoke('create-contribution-preference', {
        body: {
          organizationId: context.organizationId,
          campaignId,
          amount: Math.round(amount),
          isAnonymous: isAnonymous ?? false,
          message: message ?? null,
          payerEmail,
        },
      })
      if (error) throw error

      const initPoint = (data as { initPoint?: string } | null)?.initPoint
      if (!initPoint) throw new Error('No se pudo iniciar el pago del aporte.')
      return { initPoint }
    },

    async create(context: TenantContext, input: CreateCampaignInput) {
      const { error } = await client.rpc('create_campaign', {
        p_organization_id: context.organizationId,
        p_title: input.title,
        p_description: input.description,
        p_goal_amount: Math.round(input.goalAmount),
        p_currency: input.currency ?? 'CLP',
        p_visibility: input.visibility ?? 'members',
        p_start_date: input.startDate ?? null,
        p_end_date: input.endDate ?? null,
      })
      if (error) throw error
    },

    async updateStatus(context: TenantContext, campaignId: UUID, status: CampaignStatus) {
      const { error } = await client.rpc('update_campaign_status', {
        p_id: campaignId,
        p_organization_id: context.organizationId,
        p_status: status,
      })
      if (error) throw error
    },

    async delete(context: TenantContext, campaignId: UUID) {
      const { error } = await client.rpc('delete_campaign', { p_id: campaignId, p_organization_id: context.organizationId })
      if (error) throw error
    },

    async listPendingContributions(context: TenantContext, { limit }: CampaignListInput) {
      const { data, error } = await client
        .from('contributions')
        .select(`
          id, amount, currency, is_anonymous, message, created_at,
          campaign:campaigns!inner (id, title, raised_amount, contributor_count, organization_id),
          membership:memberships (id, person:persons (preferred_name))
        `)
        .eq('campaign.organization_id', context.organizationId)
        .eq('status', 'pending')
        .order('created_at', { ascending: true })
        .limit(Math.max(1, Math.min(limit, 200)))

      if (error) throw error

      return (data ?? []).map((item): PendingContribution => {
        const campaign = item.campaign as unknown as { id: UUID; title: string; raised_amount: number; contributor_count: number }
        const membership = item.membership as unknown as { id: UUID; person: { preferred_name: string } | null } | null
        return {
          id: item.id as UUID,
          campaignId: campaign.id,
          campaignTitle: campaign.title,
          campaignRaisedAmount: Number(campaign.raised_amount),
          campaignContributorCount: campaign.contributor_count,
          contributorMembershipId: membership?.id ?? null,
          amount: Number(item.amount),
          currency: item.currency as string,
          isAnonymous: item.is_anonymous as boolean,
          message: (item.message as string | null) ?? null,
          contributorName: item.is_anonymous ? null : (membership?.person?.preferred_name ?? null),
          createdAt: item.created_at as string,
        }
      })
    },

    async confirmContribution(context: TenantContext, contribution: PendingContribution) {
      if (!context.membershipId) throw new Error('Membership is required to confirm a contribution')

      const { error } = await client.rpc('confirm_contribution', { p_id: contribution.id, p_organization_id: context.organizationId })
      if (error) throw error
    },

    async rejectContribution(context: TenantContext, contribution: PendingContribution) {
      if (!context.membershipId) throw new Error('Membership is required to reject a contribution')

      const { error } = await client.rpc('reject_contribution', { p_id: contribution.id, p_organization_id: context.organizationId })
      if (error) throw error
    },
  }
}
